<template>
  <v-container fluid class="container">
    <Breadcrumbs :items="breadcrumbs" />

    <v-toolbar flat>
      <ejs-dropdownlist
        id="reportDropdown"
        ref="dropdown"
        :dataSource="dropdownDataSource"
        :change="onReportDropdownChange"
        v-model="reportDropdown"
        placeholder="Report Type"
        width="250"
      />
      <v-spacer />
      <v-btn color="primary" @click="excelDownloadClick">
        <v-icon left>mdi-file-excel</v-icon>
        Export to Excel
      </v-btn>
    </v-toolbar>

    <v-spacer />

    <ejs-grid
      ref="grid"
      v-if="items.length && periods.length"
      :key="`grid-${reportDropdownValue}`"
      :dataSource="items"
      :filterSettings="{type: 'Menu'}"
      :searchSettings='searchOptions'
      :allowFiltering='true'
      :allowPaging='true'
      :pageSettings='pageSettings'
      :height="560"
      :allowExcelExport="true"
      :allowTextWrap="true"
      :excelHeaderQueryCellInfo="excelHeader"
      :excelQueryCellInfo="excelQueryCellInfo"
    >
      <e-columns>
        <!-- Static columns -->
        <e-column field="id" headerText="ID" width="80" />
        <e-column field="planning_period" headerText="Planning Period" width="140" />
        <e-column field="leasing_type" headerText="Leasing Type" width="120" />
        <e-column field="segment" headerText="Segment" width="140" />
        <e-column field="customer" headerText="Customer" width="140" />
        <e-column field="product_group" headerText="Product Group" width="160" />
        <e-column field="product_name" headerText="Product Name" width="160" />
        <e-column field="channel" headerText="Channel" width="140" />
        <e-column field="currency" headerText="Currency" width="100" />
        <e-column field="maturity" headerText="Maturity" width="100" />
        <e-column field="repayment" headerText="Repayment" width="120" />
        <e-column field="interest_rate_type" headerText="Interest Rate Type" width="150" />
        <e-column field="book_value" headerText="Book Value" width="120" format="n2" />
        <e-column field="total" headerText="TIC" width="120" format="n2" />
        <e-column field="finance_volume" headerText="FV" width="120" format="n2" />
        <e-column field="ir" headerText="Interest Rate (%)" width="120" format="n2" />
        <e-column field="data_source" headerText="Source" width="100" />

        <!-- Dynamic column groups for calculation arrays -->

        <e-column textAlign="center" headerText="Principal" :allowSorting="false" :columns="bookValueColumns" />
        <e-column textAlign="center" headerText="Interest income" :allowSorting="false" :columns="depreciationColumns" />
        <e-column textAlign="center" headerText="Fee" :allowSorting="false" :columns="feeColumns" />
        <e-column textAlign="center" headerText="Rental" :allowSorting="false" :columns="rentalColumns" />
        <e-column textAlign="center" headerText="PPMV" :allowSorting="false" :columns="riskCostColumns" />
        <e-column textAlign="center" headerText="Intercalary interest income" :allowSorting="false" :columns="intercalaryColumns" />
      </e-columns>
    </ejs-grid>

    <Snackbar :text="notificationText" :type="notificationType" />

    <v-overlay :value="loader" :z-index="1000">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>
  </v-container>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import {
  GridPlugin, ExcelExport, Filter, Page,
} from '@syncfusion/ej2-vue-grids';
import Breadcrumbs from '@/components/Breadcrumbs';
import Snackbar from '@/components/Snackbar';
import api from '@/api/lbt-api';


Vue.use(GridPlugin);

export default {
  name: 'SalesDetailPositions',
  components: { Breadcrumbs, Snackbar },
  data() {
    return {
      notificationText: '',
      notificationType: null,
      loader: false,
      periods: [],
      items: [],
      allItems: [],
      reportDropdown: 'All',
      reportDropdownValue: 'all',
      dropdownDataSource: ['All', 'New Business', 'Existing Business'],
      breadcrumbs: [
        { translationKey: 'breadcrumbs_plannings', route: '/plannings', disabled: false },
        { translationKey: 'sales_details', disabled: true },
      ],
      // Dynamic column arrays for different calculation types
      depreciationColumns: [],
      marginColumns: [],
      bookValueColumns: [],
      irColumns: [],
      feeColumns: [],
      rentalColumns: [],
      riskCostColumns: [],
      intercalaryColumns: [],
      searchOptions: {
        fields: ['id', 'planning_period', 'leasing_type', 'segment', 'customer', 'product_group', 'product_name', 'channel', 'currency', 'repayment', 'interest_rate_type', 'data_source'],
        operator: 'contains',
        key: '',
        ignoreCase: true,
      },
      pageSettings: {
        pageSize: 50,
        pageSizes: [50, 100, 200, 500, 'All'],
      },
    };
  },
  computed: {
    ...mapGetters('translations', ['translate']),
    ...mapState('plannings', ['activePlanning']),
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      this.loader = true;
      try {
        // Always fetch both datasets
        const [salesPayload, loansPayload] = await Promise.all([
          api.getSalesDetailPositionsForPlanning(
            this.$route.params.planningId,
            'sales_details',
            'eur',
          ),
          api.getExistingLoansForPlanning(
            this.$route.params.planningId,
            'repayment',
            'eur',
            'all',
          ),
        ]);

        this.periods = salesPayload.periods || [];

        // Transform both datasets separately (no merging)
        const salesItems = this.transformSalesData(salesPayload.rows || []);
        const loansItems = this.transformLoansData(loansPayload.rows || []);

        // Store all items together (keeping all 800+ rows)
        this.allItems = [...salesItems, ...loansItems];

        // Apply current filter
        this.filterData();

        // Build dynamic columns after data is ready
        this.buildDynamicColumns();

        // Force grid refresh
        this.$nextTick(() => {
          if (this.$refs.grid && this.$refs.grid.ej2Instances) {
            const grid = this.$refs.grid.ej2Instances[0];
            if (grid && grid.refresh) {
              grid.refresh();
            }
          }
        });
      } catch (e) {
        this.notificationText = e.message || 'Failed to load data';
        this.notificationType = 'error';
        setTimeout(() => { this.notificationText = null; }, 3000);
      } finally {
        this.loader = false;
      }
    },

    filterData() {
      if (!this.allItems) {
        return;
      }

      switch (this.reportDropdownValue) {
        case 'new_business':
          this.items = this.allItems.filter(item => item.data_source === 'New');
          break;
        case 'existing_business':
          this.items = this.allItems.filter(item => item.data_source === 'Existing');
          break;
        case 'all':
        default:
          this.items = [...this.allItems];
          break;
      }
    },

    // Map the raw repayment value to a human-readable description.
    // This report mixes sources: New Business rows carry the textual
    // REPAYMENT_TYPE option (e.g. "Monthly"), while Existing rows carry the
    // numeric CHARGE_PERIOD value (payments per year). Remap only here.
    mapRepayment(value) {
      if (value === null || value === undefined || value === '') {
        return value;
      }
      const v = String(value).trim();
      const numericMap = {
        1: 'Yearly',
        2: 'Half-yearly',
        4: 'Quarterly',
        6: 'Every two months',
        12: 'Monthly',
      };
      return numericMap[v] !== undefined ? numericMap[v] : v;
    },

    // Collapse the interest rate type / index code into Fixed vs Variable for
    // this report only: any EURIBOR-based index is Variable, any fixed-rate
    // index (FIX, HR_FIX, ...) is Fixed; Fixed/Variable pass through unchanged.
    mapInterestRateType(value) {
      if (value === null || value === undefined || value === '') {
        return value;
      }
      const v = String(value).trim();
      const upper = v.toUpperCase();
      if (upper === 'FIXED') {
        return 'Fixed';
      }
      if (upper === 'VARIABLE') {
        return 'Variable';
      }
      if (upper.includes('EURIBOR')) {
        return 'Variable';
      }
      if (upper.includes('FIX')) {
        return 'Fixed';
      }
      return v;
    },

    transformSalesData(salesData) {
      return salesData.map((item) => {
        const transformedItem = {
          // Static fields
          id: item.id,
          planning_period: item.planning_period,
          leasing_type: item.leasing_type,
          segment: item.segment,
          customer: item.customer,
          finrep_pl: item.finrep_pl,
          finrep_risk: item.finrep_risk,
          product_group: item.product_group,
          product_name: item.product_name,
          channel: item.channel,
          currency: item.currency,
          maturity: item.maturity,
          repayment: this.mapRepayment(item.repayment),
          interest_rate_type: this.mapInterestRateType(item.interest_rate_type),
          finance_volume: item.finance_volume,
          ir: item.ir,
          book_value: item.book_value,
          total: item.total,
          data_source: 'New',
        };

        // Add period values as flat properties for each calculation type
        const calcTypes = [
          'book_values',
          'ppmv_values',
          'interest_values',
          'fee_values',
          'rental_values',
          'depo_values',
          'first_rent_values',
          'fx_values',
          'ppmvfx_values',
          'rent_values',
          'intercalary_values',
          'depofx_values',
          'gain_loss_values',
        ];

        calcTypes.forEach((calcType) => {
          if (item[calcType] && Array.isArray(item[calcType])) {
            item[calcType].forEach((value, index) => {
              transformedItem[`${calcType}_${index}`] = value;
            });
          } else {
            // Initialize with zeros if missing
            this.periods.forEach((period, index) => {
              transformedItem[`${calcType}_${index}`] = 0;
            });
          }
        });

        return transformedItem;
      });
    },

    transformLoansData(loansData) {
      return loansData.map((item) => {
        const transformedItem = {
          // Map existing loans fields to sales structure
          id: item.contract_id || item.id,
          planning_period: 'Existing',
          leasing_type: item.type || '', // Use actual leasing type (FL, OL, etc.)
          segment: item.client_segment || '',
          customer: item.customer || '',
          finrep_pl: '',
          finrep_risk: '',
          product_group: item.product || '',
          product_name: '',
          channel: '',
          currency: item.currency || '',
          maturity: '',
          repayment: this.mapRepayment(item.charge_period || ''),
          interest_rate_type: this.mapInterestRateType(item.index_code || ''),
          finance_volume: 0,
          ir: 0,
          book_value: item.balance || 0,
          total: item.principal || 0,
          data_source: 'Existing',
        };

        // Map existing loans period data to sales structure
        this.periods.forEach((period, index) => {
          // Map repayments data to sales calculation types
          transformedItem[`book_values_${index}`] = item[`balance_${index}`] || 0;
          transformedItem[`interest_values_${index}`] = item[`interest_${index}`] || 0;
          transformedItem[`rental_values_${index}`] = item[`principal_${index}`] || 0;
          transformedItem[`ppmv_values_${index}`] = item[`ppmv_${index}`] || 0;
          transformedItem[`fx_values_${index}`] = item[`fx_${index}`] || 0; // Map FX data from existing

          // Initialize other calculation types with zeros (keeping intercalary for sales only)
          const zeroTypes = [
            'fee_values',
            'depo_values',
            'first_rent_values',
            'ppmvfx_values',
            'rent_values',
            'intercalary_values', // Keep as zero for existing (only sales has this)
            'depofx_values',
            'gain_loss_values',
          ];

          zeroTypes.forEach((calcType) => {
            transformedItem[`${calcType}_${index}`] = 0;
          });
        });

        return transformedItem;
      });
    },


    // Build depreciation columns dynamically
    buildDepreciationColumns() {
      this.depreciationColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `interest_values_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-2-first' };
        } else {
          colDef.customAttributes = { class: 'group-2' };
        }
        this.depreciationColumns.push(colDef);
      });
    },
    // Build book value columns dynamically
    buildBookValueColumns() {
      this.bookValueColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `book_values_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-1-first' };
        } else {
          colDef.customAttributes = { class: 'group-1' };
        }
        this.bookValueColumns.push(colDef);
      });
    },
    // Build fee columns dynamically
    buildFeeColumns() {
      this.feeColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `fee_values_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-3-first' };
        } else {
          colDef.customAttributes = { class: 'group-3' };
        }
        this.feeColumns.push(colDef);
      });
    },

    // Build rental columns dynamically
    buildRentalColumns() {
      this.rentalColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `rental_values_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-4-first' };
        } else {
          colDef.customAttributes = { class: 'group-4' };
        }
        this.rentalColumns.push(colDef);
      });
    },

    // Build risk cost columns dynamically
    buildPPMVColumns() {
      this.riskCostColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `ppmv_values_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-5-first' };
        } else {
          colDef.customAttributes = { class: 'group-5' };
        }
        this.riskCostColumns.push(colDef);
      });
    },
    buildIntercalaryColumns() {
      this.intercalaryColumns = [];
      this.periods.forEach((period, index) => {
        const colDef = {
          field: `intercalary_values_${index}`,
          headerText: index.toString(),
          headerValueAccessor: this.lookupPeriodHeader,
          width: 80,
          format: 'n2',
          textAlign: 'Right',
          allowSorting: false,
          allowFiltering: false,
        };
        if (index === 0) {
          colDef.customAttributes = { class: 'group-6-first' };
        } else {
          colDef.customAttributes = { class: 'group-6' };
        }
        this.intercalaryColumns.push(colDef);
      });
    },
    // Build all dynamic columns
    buildDynamicColumns() {
      this.buildBookValueColumns();
      this.buildDepreciationColumns();
      this.buildFeeColumns();
      this.buildRentalColumns();
      this.buildPPMVColumns();
      this.buildIntercalaryColumns();
    },

    // Header value accessor
    lookupPeriodHeader(idx) {
      return this.periods[Number(idx)] || '';
    },

    onReportDropdownChange({ value }) {
      const mappings = {
        All: 'all',
        'New Business': 'new_business',
        'Existing Business': 'existing_business',
      };
      this.reportDropdownValue = mappings[value] || 'all';
      this.filterData();
    },

    async excelDownloadClick() {
      const grid = document.getElementsByClassName('e-grid')[0].ej2_instances[0];
      if (!grid) {
        return;
      }

      const suffixByHeader = {
        Principal: '_P',
        'Interest income': '_II',
        Fee: '_F',
        Rental: '_R',
        PPMV: '_PPMV',
        'Intercalary interest income': '_III',
      };

      // Flatten stacked headers in place so Excel gets a single header row
      // (no Principal / Interest income / ... parent row) and period columns
      // are suffixed so the six groups stay distinguishable.
      const originalColumns = grid.columns;
      const flatColumns = [];
      const headerTextSnapshots = [];

      originalColumns.forEach((col) => {
        if (col.columns && col.columns.length) {
          const suffix = suffixByHeader[col.headerText] || '';
          col.columns.forEach((child) => {
            const m = String(child.field || '').match(/_(\d+)$/);
            const idx = m ? parseInt(m[1], 10) : 0;
            const periodLabel = this.periods[idx] || child.headerText || '';
            headerTextSnapshots.push({ column: child, headerText: child.headerText });
            // eslint-disable-next-line no-param-reassign
            child.headerText = `${periodLabel}${suffix}`;
            flatColumns.push(child);
          });
        } else {
          flatColumns.push(col);
        }
      });

      grid.columns = flatColumns;
      try {
        await grid.excelExport({
          exportType: 'AllPages',
          fileName: `Sales Detail Positions - ${this.reportDropdown} - Planning - ${this.activePlanning.year}-${this.activePlanning.month}.xlsx`,
        });
      } finally {
        headerTextSnapshots.forEach((snap) => {
          // eslint-disable-next-line no-param-reassign
          snap.column.headerText = snap.headerText;
        });
        grid.columns = originalColumns;
        if (typeof grid.refreshColumns === 'function') {
          grid.refreshColumns();
        }
      }
    },

    excelHeader(args) {
      const { cell } = args;
      const col = args.gridCell && args.gridCell.column;
      if (col && col.field && col.field.includes('_values_')) {
        if (typeof cell.value === 'string' && /^\d+$/.test(cell.value)) {
          cell.value = this.lookupPeriodHeader(cell.value);
        }
      }
    },

    excelQueryCellInfo(args) {
      const col = args.column;
      if (!col.customAttributes || !col.customAttributes.class) {
        return;
      }

      const className = col.customAttributes.class;
      const isAltRow = args.data && args.data.index % 2 === 1;

      // Define colors for each group
      const groupColors = {
        'group-1': { normal: '#fff9f5', alt: '#fff5eb' },
        'group-1-first': { normal: '#fff9f5', alt: '#fff5eb', border: true },
        'group-2': { normal: '#f5fdf5', alt: '#edfaed' },
        'group-2-first': { normal: '#f5fdf5', alt: '#edfaed', border: true },
        'group-3': { normal: '#fff9f5', alt: '#fff5eb' },
        'group-3-first': { normal: '#fff9f5', alt: '#fff5eb', border: true },
        'group-4': { normal: '#f5fdf5', alt: '#edfaed' },
        'group-4-first': { normal: '#f5fdf5', alt: '#edfaed', border: true },
        'group-5': { normal: '#fff9f5', alt: '#fff5eb' },
        'group-5-first': { normal: '#fff9f5', alt: '#fff5eb', border: true },
        'group-6': { normal: '#f5fdf5', alt: '#edfaed' },
        'group-6-first': { normal: '#f5fdf5', alt: '#edfaed', border: true },
      };

      if (groupColors[className]) {
        const colors = groupColors[className];
        const bgColor = isAltRow ? colors.alt : colors.normal;

        // eslint-disable-next-line no-param-reassign
        args.style = {
          backColor: bgColor,
        };

        // Add left border for first columns
        if (colors.border) {
          // eslint-disable-next-line no-param-reassign
          args.style.leftBorderColor = '#1976d2';
          // eslint-disable-next-line no-param-reassign
          args.style.leftBorderStyle = 'Thick';
        }
      }
    },
  },
  provide: { grid: [ExcelExport, Filter, Page] },
};
</script>

<style>
@import '~@syncfusion/ej2-vue-treegrid/styles/material.css';

.e-grid .e-headercell.highlight-cell,
.highlight-cell {
  background: #eeeeee;
}

.e-valid-input {
  margin-right: 10px;
}

.e-headercelldiv {
  text-align: center !important;
}

/* Column group dividers - add thicker borders between groups */
.e-grid .e-stackedheadercell {
  border-left: 3px solid #1976d2 !important;
  background: linear-gradient(to bottom, #e3f2fd 0%, #bbdefb 100%);
  font-weight: 600;
}

/* First column group doesn't need left border */
.e-grid .e-stackedheadercell:first-of-type {
  border-left: none !important;
}

/* Alternating background colors for column groups */
.e-grid .e-stackedheadercell:nth-child(odd) {
  background: linear-gradient(to bottom, #fff3e0 0%, #ffe0b2 100%);
}

.e-grid .e-stackedheadercell:nth-child(even) {
  background: linear-gradient(to bottom, #e8f5e9 0%, #c8e6c9 100%);
}

/* Apply dividers and background to first column of each group */
.e-grid .e-rowcell.group-1-first,
.e-grid .e-rowcell.group-2-first,
.e-grid .e-rowcell.group-3-first,
.e-grid .e-rowcell.group-4-first,
.e-grid .e-rowcell.group-5-first,
.e-grid .e-rowcell.group-6-first {
  border-left: 3px solid #1976d2 !important;
}

/* Apply background colors to all columns in each group */
/* Group 1 - Principal */
.e-grid .e-row .e-rowcell.group-1,
.e-grid .e-row .e-rowcell.group-1-first {
  background-color: #fff9f5 !important;
}
.e-grid .e-altrow .e-rowcell.group-1,
.e-grid .e-altrow .e-rowcell.group-1-first {
  background-color: #fff5eb !important;
}

/* Group 2 - Interest income */
.e-grid .e-row .e-rowcell.group-2,
.e-grid .e-row .e-rowcell.group-2-first {
  background-color: #f5fdf5 !important;
}
.e-grid .e-altrow .e-rowcell.group-2,
.e-grid .e-altrow .e-rowcell.group-2-first {
  background-color: #edfaed !important;
}

/* Group 3 - Fee */
.e-grid .e-row .e-rowcell.group-3,
.e-grid .e-row .e-rowcell.group-3-first {
  background-color: #fff9f5 !important;
}
.e-grid .e-altrow .e-rowcell.group-3,
.e-grid .e-altrow .e-rowcell.group-3-first {
  background-color: #fff5eb !important;
}

/* Group 4 - Rental */
.e-grid .e-row .e-rowcell.group-4,
.e-grid .e-row .e-rowcell.group-4-first {
  background-color: #f5fdf5 !important;
}
.e-grid .e-altrow .e-rowcell.group-4,
.e-grid .e-altrow .e-rowcell.group-4-first {
  background-color: #edfaed !important;
}

/* Group 5 - PPMV */
.e-grid .e-row .e-rowcell.group-5,
.e-grid .e-row .e-rowcell.group-5-first {
  background-color: #fff9f5 !important;
}
.e-grid .e-altrow .e-rowcell.group-5,
.e-grid .e-altrow .e-rowcell.group-5-first {
  background-color: #fff5eb !important;
}

/* Group 6 - Intercalary */
.e-grid .e-row .e-rowcell.group-6,
.e-grid .e-row .e-rowcell.group-6-first {
  background-color: #f5fdf5 !important;
}
.e-grid .e-altrow .e-rowcell.group-6,
.e-grid .e-altrow .e-rowcell.group-6-first {
  background-color: #edfaed !important;
}
</style>
